import React, {type CSSProperties} from 'react';
import {MaxWidth, PageMeta} from '../../types/content.types';

type CustomCssValue = string & {};
type PageWithSidebarLayoutGridBreakpoint = 'lg' | 'xl';

export const pageWithSidebarLayoutTokens = {
    gridTemplateColumns: {
        default: {
            lg: 'minmax(0, 1fr) 250px',
            xl: 'minmax(0, 1fr) 260px',
        },
        'main-3-sidebar-1': 'minmax(0, 3fr) minmax(0, 1fr)',
        'main-2-sidebar-1': 'minmax(0, 2fr) minmax(0, 1fr)',
        balanced: 'minmax(0, 1fr) minmax(0, 1fr)',
        'main-2-sidebar-3': 'minmax(0, 2fr) minmax(0, 3fr)',
        'main-90-sidebar-10': 'minmax(0, 9fr) minmax(0, 1fr)',
    },
    mainBackgroundColors: {
        transparent: 'transparent',
        white: '#ffffff',
        soft: '#f8fafc',
        muted: '#f1f5f9',
    },
    mainBorderRadius: {
        none: '0px',
        sm: '4px',
        md: '8px',
        lg: '12px',
        xl: '16px',
    },
} as const;

export type PageWithSidebarLayoutGridTemplateColumnsToken =
    keyof typeof pageWithSidebarLayoutTokens.gridTemplateColumns;

export type PageWithSidebarLayoutGridTemplateColumns =
    | PageWithSidebarLayoutGridTemplateColumnsToken
    | CustomCssValue
    | Partial<Record<PageWithSidebarLayoutGridBreakpoint, PageWithSidebarLayoutGridTemplateColumnsToken | CustomCssValue>>;

export type PageWithSidebarLayoutMainBackgroundColor =
    | keyof typeof pageWithSidebarLayoutTokens.mainBackgroundColors
    | CustomCssValue;

export type PageWithSidebarLayoutMainBorderRadius =
    | keyof typeof pageWithSidebarLayoutTokens.mainBorderRadius
    | number
    | CustomCssValue;

export type PageWithSidebarLayoutProps = {
    main: React.ReactNode;
    sidebar?: React.ReactNode;
    maxWidth?: MaxWidth;
    pageMeta?: PageMeta;
    gridTemplateColumns?: PageWithSidebarLayoutGridTemplateColumns;
    mainBackgroundColor?: PageWithSidebarLayoutMainBackgroundColor;
    mainBorderRadius?: PageWithSidebarLayoutMainBorderRadius;
};

const maxWidthMap: Record<MaxWidth, string> = {
    md: 'max-w-3xl',
    lg: 'max-w-4xl',
    xl: 'max-w-6xl',
    '2xl': 'max-w-7xl',
};

type ResolvedGridTemplateColumns = Record<PageWithSidebarLayoutGridBreakpoint, string>;

type PageWithSidebarLayoutCssVars = CSSProperties &
    Record<`--page-with-sidebar-layout-${string}`, string | undefined>;

const defaultGridTemplateColumns = pageWithSidebarLayoutTokens.gridTemplateColumns.default;

const isGridTemplateColumnsToken = (
    value: string,
): value is PageWithSidebarLayoutGridTemplateColumnsToken => (
    value in pageWithSidebarLayoutTokens.gridTemplateColumns
);

const resolveGridTemplateColumnsToken = (
    token: PageWithSidebarLayoutGridTemplateColumnsToken,
): ResolvedGridTemplateColumns => {
    const value = pageWithSidebarLayoutTokens.gridTemplateColumns[token];

    if (typeof value === 'string') {
        return {
            lg: value,
            xl: value,
        };
    }

    return value;
};

const resolveGridTemplateColumnsValue = (
    value: PageWithSidebarLayoutGridTemplateColumnsToken | CustomCssValue,
): ResolvedGridTemplateColumns => {
    if (isGridTemplateColumnsToken(value)) {
        return resolveGridTemplateColumnsToken(value);
    }

    return {
        lg: value,
        xl: value,
    };
};

const resolveGridTemplateColumns = (
    value?: PageWithSidebarLayoutGridTemplateColumns,
): ResolvedGridTemplateColumns => {
    if (!value) {
        return defaultGridTemplateColumns;
    }

    if (typeof value === 'string') {
        return resolveGridTemplateColumnsValue(value);
    }

    const lgColumns = value.lg
        ? resolveGridTemplateColumnsValue(value.lg)
        : defaultGridTemplateColumns;
    const xlColumns = value.xl
        ? resolveGridTemplateColumnsValue(value.xl)
        : lgColumns;

    return {
        lg: lgColumns.lg,
        xl: xlColumns.xl,
    };
};

const resolveMainBackgroundColor = (
    value?: PageWithSidebarLayoutMainBackgroundColor,
) => {
    if (!value) {
        return pageWithSidebarLayoutTokens.mainBackgroundColors.transparent;
    }

    if (value in pageWithSidebarLayoutTokens.mainBackgroundColors) {
        return pageWithSidebarLayoutTokens.mainBackgroundColors[
            value as keyof typeof pageWithSidebarLayoutTokens.mainBackgroundColors
        ];
    }

    return value;
};

const resolveMainBorderRadius = (
    value?: PageWithSidebarLayoutMainBorderRadius,
) => {
    if (value === undefined) {
        return pageWithSidebarLayoutTokens.mainBorderRadius.none;
    }

    if (typeof value === 'number') {
        return `${value}px`;
    }

    if (value in pageWithSidebarLayoutTokens.mainBorderRadius) {
        return pageWithSidebarLayoutTokens.mainBorderRadius[
            value as keyof typeof pageWithSidebarLayoutTokens.mainBorderRadius
        ];
    }

    return value;
};

const pageWithSidebarLayoutCss = `
.page-with-sidebar-layout-grid {
    display: grid;
    grid-template-columns: minmax(0, 1fr);
    gap: 2rem;
}

.page-with-sidebar-layout-main {
    min-width: 0;
    background: var(--page-with-sidebar-layout-main-background);
    border-radius: var(--page-with-sidebar-layout-main-border-radius);
}

.page-with-sidebar-layout-sidebar {
    order: 9999;
}

.page-with-sidebar-layout-sidebar > * + * {
    margin-top: 1.5rem;
}

@media (min-width: 1024px) {
    .page-with-sidebar-layout-grid {
        grid-template-columns: var(--page-with-sidebar-layout-grid-template-columns-lg);
    }

    .page-with-sidebar-layout-sidebar {
        order: initial;
    }
}

@media (min-width: 1280px) {
    .page-with-sidebar-layout-grid {
        grid-template-columns: var(--page-with-sidebar-layout-grid-template-columns-xl);
    }
}
`;

const PageWithSidebarLayout = ({
                                   main,
                                   sidebar,
                                   maxWidth = 'xl',
                                   pageMeta,
                                   gridTemplateColumns,
                                   mainBackgroundColor,
                                   mainBorderRadius,
                               }: PageWithSidebarLayoutProps) => {
    const resolvedGridTemplateColumns = resolveGridTemplateColumns(gridTemplateColumns);
    const gridStyle: PageWithSidebarLayoutCssVars = {
        '--page-with-sidebar-layout-grid-template-columns-lg': resolvedGridTemplateColumns.lg,
        '--page-with-sidebar-layout-grid-template-columns-xl': resolvedGridTemplateColumns.xl,
    };
    const mainStyle: PageWithSidebarLayoutCssVars = {
        '--page-with-sidebar-layout-main-background': resolveMainBackgroundColor(mainBackgroundColor),
        '--page-with-sidebar-layout-main-border-radius': resolveMainBorderRadius(mainBorderRadius),
    };

    return (
        <div className="w-full px-4 sm:px-6 lg:px-8">
            <style>{pageWithSidebarLayoutCss}</style>
            <div
                className={`mx-auto ${maxWidthMap[maxWidth]}`}
            >
                <div
                    className="page-with-sidebar-layout-grid"
                    style={gridStyle}
                >
                    {/* Main content */}
                    <main
                        id="main-content"
                        className="page-with-sidebar-layout-main"
                        style={mainStyle}
                    >
                        {main}
                    </main>

                    {/* Sidebar */}
                    {sidebar && (
                        <aside
                            className="page-with-sidebar-layout-sidebar"
                        >
                            {sidebar}
                        </aside>
                    )}
                </div>
            </div>
        </div>
    );
};

export default PageWithSidebarLayout;
